'use client';
import {
  ArrowLeftStartOnRectangleIcon,
  Cog6ToothIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { Dropdown } from '@jci/components/Dropdown';
import type { DealerBilling } from '@jci/core/db/schemas/main.schema';
import type { PmDealer } from '@jci/core/siteServicesApi/jc2Schemas/jc2Dealer';
import cn from 'classnames';
import type { Session } from 'next-auth';
import { useAction } from 'next-safe-action/hooks';
import Image from 'next/image';
import Link from 'next/link';
import { useSelectedLayoutSegments } from 'next/navigation';
import { useEffect } from 'react';
import { isApprovedDealer, userRoles } from '../../../core/src/utils/userRoles';
import { HOME_PAGE, WEBSITE_NAME } from '../config';
import { env } from '../env.mjs';
import { signOutAction } from '../server/actions';
import { getCookie, setCookie } from '../utils/cookies';
import { canViewServicePlans } from './dashboard/dealer/servicePlans/utils';

type Props = {
  session: Session | null;
  activeDealer?: PmDealer | null;
  activeDealerBilling?: DealerBilling;
};

const activeDealerCookieName = (userId: string) => `${userId}activeDealer`;

type NavItem = {
  text: string;
  shouldOmit?: boolean;
  linkTo: string;
};

export const Navbar = ({
  activeDealer,
  activeDealerBilling,
  session,
}: Props) => {
  const signOut = useAction(signOutAction);
  const urlSegments = useSelectedLayoutSegments();
  const url = `/${urlSegments.join('/')}`;

  // set active dealer cookie if not set
  useEffect(() => {
    if (!session?.user.id) return;
    const activeDealerCookie = activeDealerCookieName(session.user.id);
    if (
      getCookie(activeDealerCookie) === undefined &&
      activeDealer !== undefined
    )
      setCookie(activeDealerCookie, activeDealer?.dealer_id ?? 'null');
  }, [session?.user.id, activeDealer]);

  const isApproved = activeDealer && isApprovedDealer(activeDealer);
  const dealerRole = activeDealer && userRoles[activeDealer.role];

  const signedInMenuItems = [
    {
      text: 'Service Plans',
      linkTo: '/dashboard/dealer/servicePlans',
      shouldOmit: !canViewServicePlans(activeDealerBilling),
    },
    {
      text: 'Service Accounts',
      linkTo: '/dashboard/dealer/service',
    },
    {
      text: 'Invoices',
      linkTo: '/dashboard/dealer/invoices',
    },
    {
      text: 'Dealer Profile',
      linkTo: '/dashboard/dealer/profile',
    },
  ] satisfies NavItem[];

  const signedInMenu = (
    <div className="flex items-center gap-3">
      {isApproved && (
        <div className="tabs tabs-bordered flex justify-evenly gap-4">
          {signedInMenuItems
            .filter((item) => !item.shouldOmit)
            .map((item) => {
              const isActive = item.linkTo === url;
              return (
                <Link
                  key={item.text}
                  className={cn({
                    'flex-1': true,
                    'border-b-2 border-white px-4 opacity-80': !isActive,
                    'tab font-semibold text-neutral-500': isActive,
                    'whitespace-nowrap text-lg tracking-tight': true,
                  })}
                  href={item.linkTo}
                >
                  {item.text}
                </Link>
              );
            })}
        </div>
      )}
      <Dropdown
        className="dropdown-end"
        trigger={
          <div
            data-testid="userMenu"
            className="cursor-pointer border-b-0 border-white p-2 text-neutral-600 opacity-90"
          >
            <UserCircleIcon className="size-8" />
          </div>
        }
      >
        <div className="flex flex-col items-start gap-2 px-8 pb-6 pt-5 tracking-tight">
          <div className="flex flex-col">
            <p className="text-lg font-semibold text-gray-500">
              {session?.user.name ?? ''}
            </p>
            <p className="text-gray-400">{session?.user.email ?? ''}</p>
            {dealerRole && (
              <p className="mt-1 capitalize text-gray-700 opacity-80">
                {dealerRole}
              </p>
            )}
          </div>
          <div className="divider my-1" />
          <Link
            className="link-hover link flex items-center gap-3"
            href={`${env.NEXT_PUBLIC_OKTA_ORG_URL}/enduser/settings`}
            target="_blank"
          >
            <Cog6ToothIcon className="size-5 opacity-90" />
            User profile
          </Link>
          <div
            onClick={() => signOut.execute()}
            className="item-center link-hover link link-primary flex gap-3 whitespace-nowrap"
          >
            {signOut.isExecuting ? (
              <span className="loading loading-spinner scale-75"></span>
            ) : (
              <ArrowLeftStartOnRectangleIcon className="size-5 opacity-90" />
            )}
            Sign out
          </div>
        </div>
      </Dropdown>
    </div>
  );

  return (
    <div
      data-testid="navbar"
      className="flex w-full justify-center bg-white shadow-sm"
    >
      <div className="flex w-full max-w-screen-2xl items-center justify-between p-3">
        <Link href={HOME_PAGE} className="flex items-center gap-3">
          <Image src="/jci-logo.png" alt="JCI Logo" width={34} height={34} />
          <p className="whitespace-nowrap text-lg font-semibold tracking-tight">
            {WEBSITE_NAME}
          </p>
        </Link>
        {session ? (
          signedInMenu
        ) : (
          <div className="btn text-lg opacity-0">
            {/* makes the navbar the same height regardless of auth status */}l
          </div>
        )}
      </div>
    </div>
  );
};
